import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import { CustomerInfo } from '@/types/user';
import { formatDate } from '@/utilities/formatDate';
import Image from 'next/image';
import { useState } from 'react';

const SubscriptionPlan = (props: {
  id: string;
  plan: string;
  cost: string;
  interval: string;
  expirationDate: string | null;
  nextBillingDate: string | null;
  status: string;
  customerInfo: CustomerInfo;
}) => {

  const [isSuccess, setIsSuccess] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [action, setAction] = useState<'upgrade' | 'downgrade' | 'cancel' | null>(null)
  const [changeMembershipfeedbackMessage, setchangeMembershipFeedbackMessage] = useState<{ type: 'error' | 'success', message: string } | null>(null);


  const deleteMembership = async () => {
    setIsSuccess(false);
    fetch('/api/deleteMembership', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Access-Control-Allow-Origin': '*',
        'Accept': '*/*'
      },
      body: JSON.stringify(
        { contractId: props.id }
      )
    })
      .then(async () => {
        setIsSuccess(true);
        setIsModalOpen(false)
        window.location.reload();
      })
      .catch((err) => {
        setIsModalOpen(false)
        setIsSuccess(false);
        console.error(err);
      })
  };

  const handleChangeMembershipPlan = async () => {
    fetch('/api/changeMembershipPlan', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: (props.customerInfo.firstName || '-') + ' ' + (props.customerInfo.lastName || '-'),
        email: props.customerInfo.email || '-',
        phone: props.customerInfo.phone || '-',
        subscriptionId: props.id,
        action
      })
    }).then((res) => {
      if (res.status === 200) {
        setIsModalOpen(false)
        setchangeMembershipFeedbackMessage({ type: 'success', message: 'Request sent successfully' })
      } else {
        setchangeMembershipFeedbackMessage({ type: 'error', message: 'Something went wrong, please reload the page and try again' })
        setIsModalOpen(false)
      }
    }).catch((err) => {
      setchangeMembershipFeedbackMessage({ type: 'error', message: 'Something went wrong, please reload the page and try again' })
      setIsModalOpen(false)
      console.error(err)
    }
    );
  }


  return (
    <div className="flex flex-col">
      <h4 className="font-header italic leading-8 font-normal md:text-center lg:text-left">Your Subscription</h4>
      <div className="flex mt-4">
        <p className="mr-16">Plan:</p>
        <p>{props.plan.toLowerCase() === 'month' ? 'Monthly' : 'Annual'}</p>
      </div>
      <div className="flex mt-1.5">
        <p className="mr-14">Cost:</p>
        <p>
          ${props.cost}/{props.interval.toLowerCase()}
        </p>
      </div>
      {props.status.toLowerCase() === 'cancelled' && <div className="flex mt-4 lg:mt-14">
        <Image src="/exclamation.svg" width={14} height={20} alt="exclamation" />
        <p className="font-semibold text-coral lg:text-base ml-2">
          You have cancelled your membership
        </p>
      </div>}
      {props.expirationDate && <div className="flex mt-4 lg:mt-14">
        <Image src="/exclamation.svg" width={14} height={20} alt="exclamation" />
        <p className="font-semibold text-coral lg:text-base ml-2">
          Your membership is valid till {props.expirationDate}
        </p>
      </div>}
      {props.status.toLowerCase() !== 'cancelled' && props.nextBillingDate && <div className="flex mt-4 lg:mt-14">
        <Image src="/icons/white-exclamation.svg" width={14} height={20} alt="exclamation" />
        <p className="font-semibold text-white lg:text-base ml-2">
          Your membership due for renewal {formatDate(props.nextBillingDate)}
        </p>
      </div>}
      {changeMembershipfeedbackMessage === null ?
        (
          <Button className="text-onyx py-2 my-5 bg-cloud uppercase" onClick={() => { setIsModalOpen(true); setAction(props.plan === 'annual' ? 'downgrade' : 'upgrade') }}>
            {props.plan === 'annual' ? 'Downgrade' : 'Upgrade'} to {props.plan === 'annual' ? 'monthly' : 'annual'} plan
          </Button>
        ) : (
          <div className='flex flex-row mt-4 items-center'>
            <Image
              src={changeMembershipfeedbackMessage.type === 'success' ? "/icons/green-check-mark.svg" : "/exclamation.svg"}
              alt={changeMembershipfeedbackMessage.type === 'success' ? "Correct" : "error"}
              width={12}
              height={9}
            />
            <p className='font-normal text-base text-mint ml-4'>{changeMembershipfeedbackMessage.message}</p>
          </div>
        )
      }
      {
        props.status.toLowerCase() !== 'cancelled' && <div className='my-5 cursor-pointer' onClick={() => { setIsModalOpen(true); setAction('cancel') }}>
          <a className="uppercase underline text-sm">Cancel {props.plan.toLowerCase() === 'month' ? 'Monthly' : 'Annual'} plan</a>
        </div>
      }
      {
        isSuccess && <div className='flex flex-row mt-4 items-center'>
          <Image
            src="/icons/green-check-mark.svg"
            alt="Correct"
            width={12}
            height={9}
          />
          <p className='text-base text-mint ml-4'>Confirmation email sent</p>
        </div>
      }
      <Modal isOpen={isModalOpen}>
        <div className='flex flex-col justify-center items-center'>
          {action === 'cancel' && (
            <>
              <h4 className='font-medium text-[24px] italic leading-8 text-center'>Are you sure you want <br /> to cancel your memberhip?</h4>
              <p className='mt-5 mb-5 font-normal text-base text-center'>
                Your subscription will end once your current<br /> plan expires. This cannot be undone.
              </p>
              <Button className="bg-onyx text-white mb-5 w-2/3 uppercase" onClick={() => deleteMembership()}>confirm</Button>
            </>
          )}
          {action !== 'cancel' && action && (
            <>
              <h4 className='font-medium text-[24px] italic leading-8 text-center'>{`Are you sure you wantto ${action} your memberhip?`}</h4>
              <p className='mt-5 mb-5 font-normal text-base text-center'>
                Our membership team has been informed. We will notify you as soon as your membership has been updated.<br />
                If you have any questions or concerns, please contact us as <a href='mailto:care@dailypolished.com' className='underline'>care@dailypolished.com</a>.
              </p>
              <Button className="bg-onyx text-white mb-5 w-2/3 uppercase" onClick={() => handleChangeMembershipPlan()}>confirm</Button>
            </>
          )}
          <Button className="border border-onyx text-onyx mb-5 w-2/3 uppercase" onClick={() => setIsModalOpen(false)}>cancel</Button>
        </div>
      </Modal >
    </div >
  );
}

export default SubscriptionPlan;
